// photoUpload.js - Client-side photo upload utility
// Handles compression, EXIF extraction, upload with retry, and offline queuing
import imageCompression from 'browser-image-compression';

var compressionOptions = {
    maxSizeMB: 2,
    maxWidthOrHeight: 2048,
    useWebWorker: true,
    fileType: 'image/jpeg'
};

// Retry a function with exponential backoff
function retryWithBackoff(fn, maxRetries, baseDelay) {
    maxRetries = maxRetries || 3;
    baseDelay = baseDelay || 1000;

    return new Promise(function(resolve, reject) {
        var attempt = 0;

        function tryOnce() {
            attempt++;
            fn().then(resolve).catch(function(err) {
                if (attempt >= maxRetries) {
                    reject(err);
                    return;
                }
                var delay = baseDelay * Math.pow(2, attempt - 1);
                console.log('Upload attempt ' + attempt + ' failed, retrying in ' + delay + 'ms...');
                setTimeout(tryOnce, delay);
            });
        }

        tryOnce();
    });
}

// Compress an image file, returns a compressed Blob
export function compressImage(file) {
    return imageCompression(file, compressionOptions)
        .catch(function(error) {
            console.log('Compression failed, using original:', error);
            return file;
        });
}

// Extract GPS coordinates from original file BEFORE compression (compression strips EXIF)
export function extractExifGps(file) {
    return new Promise(function(resolve) {
        var reader = new FileReader();
        reader.onload = function(e) {
            var buffer = e.target.result;
            // Try to parse EXIF using a lightweight approach
            // We'll use the exifr library if available, but since it's a server-side dep,
            // we do a simple check for JPEG EXIF GPS data
            try {
                var view = new DataView(buffer);
                // Check for JPEG
                if (view.getUint16(0) !== 0xFFD8) {
                    resolve({ latitude: null, longitude: null });
                    return;
                }

                // For simplicity on client side, we'll pass null and let the server
                // extract EXIF from the original upload. The server fetches from GCS
                // and parses EXIF there.
                resolve({ latitude: null, longitude: null });
            } catch (err) {
                resolve({ latitude: null, longitude: null });
            }
        };
        reader.onerror = function() {
            resolve({ latitude: null, longitude: null });
        };
        reader.readAsArrayBuffer(file.slice(0, 65536)); // read first 64KB for EXIF
    });
}

// Save a pending upload to localStorage for retry on reconnect
function savePendingUpload(uploadData) {
    try {
        var pending = JSON.parse(localStorage.getItem('pendingPhotoUploads') || '[]');
        pending.push(uploadData);
        localStorage.setItem('pendingPhotoUploads', JSON.stringify(pending));
    } catch (e) {
        console.log('Could not save pending upload:', e);
    }
}

// Remove a completed pending upload from localStorage
function removePendingUpload(objectKey) {
    try {
        var pending = JSON.parse(localStorage.getItem('pendingPhotoUploads') || '[]');
        pending = pending.filter(function(item) { return item.objectKey !== objectKey; });
        localStorage.setItem('pendingPhotoUploads', JSON.stringify(pending));
    } catch (e) {
        console.log('Could not remove pending upload:', e);
    }
}

// Main upload function with retry and offline queuing
// Returns: { success, photoId, photoUrl, pending }
export async function uploadPhoto(params) {
    var file = params.file;
    var email = params.email;
    var bloomVoteId = params.bloomVoteId;
    var zipcode = params.zipcode;
    var caption = params.caption;

    try {
        // Step 1: Get presigned URL from server (with retry)
        var presignedData = await retryWithBackoff(function() {
            var queryParams = new URLSearchParams({
                filename: file.name || 'photo.jpg',
                contentType: file.type || 'image/jpeg',
                email: email || ''
            });
            return fetch('/api/photos/presigned-url?' + queryParams.toString())
                .then(function(response) {
                    if (!response.ok) throw new Error('Failed to get upload URL');
                    return response.json();
                })
                .then(function(data) {
                    if (!data.success) throw new Error(data.error || 'Failed to get upload URL');
                    return data;
                });
        }, 3, 1000);

        // Step 2: Upload file directly to GCS (with retry)
        await retryWithBackoff(function() {
            return fetch(presignedData.uploadUrl, {
                method: 'PUT',
                headers: { 'Content-Type': file.type || 'image/jpeg' },
                body: file
            }).then(function(response) {
                if (!response.ok) throw new Error('Upload to storage failed');
                return response;
            });
        }, 3, 2000);

        // Step 3: Confirm upload with server (with retry)
        var confirmData = await retryWithBackoff(function() {
            return fetch('/api/photos/confirm', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    objectKey: presignedData.objectKey,
                    bloom_vote_id: bloomVoteId,
                    email: email,
                    zipcode: zipcode,
                    original_filename: file.name || 'photo.jpg',
                    file_size_bytes: file.size,
                    mime_type: file.type || 'image/jpeg',
                    caption: caption
                })
            }).then(function(response) {
                if (!response.ok) throw new Error('Failed to confirm upload');
                return response.json();
            });
        }, 3, 1000);

        return {
            success: true,
            photoId: confirmData.photoId,
            photoUrl: confirmData.photoUrl,
            isDuplicate: confirmData.isDuplicate,
            pending: false
        };

    } catch (error) {
        console.log('Photo upload failed after retries:', error);

        // Save to localStorage for retry later
        savePendingUpload({
            timestamp: Date.now(),
            email: email,
            bloomVoteId: bloomVoteId,
            zipcode: zipcode,
            caption: caption,
            fileName: file.name,
            error: error.message
        });

        return {
            success: false,
            error: error.message,
            pending: true
        };
    }
}

// Retry any pending uploads that failed due to connectivity
export function retryPendingUploads() {
    try {
        var pending = JSON.parse(localStorage.getItem('pendingPhotoUploads') || '[]');
        if (pending.length === 0) return;

        // Clean up old entries (older than 24 hours)
        var now = Date.now();
        var fresh = pending.filter(function(item) {
            return (now - item.timestamp) < 24 * 60 * 60 * 1000;
        });
        if (fresh.length !== pending.length) {
            localStorage.setItem('pendingPhotoUploads', JSON.stringify(fresh));
        }

        console.log(fresh.length + ' pending photo upload(s) found. These require re-selection of the photo file to retry.');
    } catch (e) {
        // ignore
    }
}
